import React, { useEffect, useState, useCallback } from "react";
import GameShell from "../components/GameShell";
import ClaimFlow from "../components/ClaimFlow";
import PlayLimitNotice from "../components/PlayLimitNotice";
import { api } from "../lib/api";
import { getDeviceId } from "../lib/device";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const PAIRS = ["🧋", "🥞", "🧇", "🍔", "🍩", "🍦"];
const shuffle = (a) => {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [r[i], r[j]] = [r[j], r[i]];
  }
  return r;
};

export default function MemoryPage() {
  const [config, setConfig] = useState(null);
  const [canPlay, setCanPlay] = useState(true);
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState(false);
  const [cards, setCards] = useState([]);       // {key, emoji, matched}
  const [flipped, setFlipped] = useState([]);   // indices face visible (max 2)
  const [lock, setLock] = useState(false);
  const [moves, setMoves] = useState(0);
  const [prize, setPrize] = useState(null);
  const [result, setResult] = useState(null);
  const deviceId = getDeviceId();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/config/memory");
      setConfig(data);
      const cp = await api.get(`/can-play/${encodeURIComponent(deviceId)}?game=memory`);
      setCanPlay(cp.data.can_play);
    } catch (e) {
      toast.error("Impossible de charger le jeu");
    }
  }, [deviceId]);

  useEffect(() => { load(); }, [load]);

  const start = async () => {
    if (starting || !canPlay) { if (!canPlay) toast("🌙 Un seul jeu par jour — reviens demain !"); return; }
    setStarting(true);
    try {
      const { data } = await api.post("/memory", { device_id: deviceId });
      setPrize(data);
      setCards(shuffle([...PAIRS, ...PAIRS]).map((emoji, i) => ({ key: i, emoji, matched: false })));
      setFlipped([]); setMoves(0); setStarted(true); setCanPlay(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      if (e?.response?.status === 429) setCanPlay(false);
    } finally {
      setStarting(false);
    }
  };

  const flip = (i) => {
    if (lock || cards[i].matched || flipped.includes(i) || flipped.length === 2) return;
    const nf = [...flipped, i];
    setFlipped(nf);
    if (nf.length === 2) {
      setLock(true);
      setMoves((m) => m + 1);
      const [a, b] = nf;
      if (cards[a].emoji === cards[b].emoji) {
        setTimeout(() => {
          setCards((cs) => cs.map((c, idx) => (idx === a || idx === b ? { ...c, matched: true } : c)));
          setFlipped([]); setLock(false);
        }, 450);
      } else {
        setTimeout(() => { setFlipped([]); setLock(false); }, 850);
      }
    }
  };

  // Partie gagnée : toutes les paires trouvées → on révèle le lot.
  useEffect(() => {
    if (started && cards.length && cards.every((c) => c.matched)) {
      const t = setTimeout(() => setResult(prize), 500);
      return () => clearTimeout(t);
    }
  }, [cards, started, prize]);

  const reset = () => { setStarted(false); setCards([]); setPrize(null); setResult(null); };

  return (
    <GameShell title={config?.title || "Memory Gourmand"} subtitle={config?.subtitle || "Trouve les paires !"}>
      {!config ? (
        <Loader2 className="animate-spin text-white mt-6" size={44} />
      ) : !started ? (
        <div className="flex flex-col items-center">
          <div className="bg-white text-ink rounded-3xl p-5 mt-2 max-w-sm w-full text-center" style={{ boxShadow: "0 8px 0 #a81a0c" }}>
            <p className="font-display font-bold text-lg">Retrouve les 6 paires 🧠</p>
            <p className="text-sm text-ink/60 mt-1">Associe les gourmandises identiques. Termine la grille et gagne ton cadeau !</p>
          </div>
          <button data-testid="memory-start" onClick={start} disabled={starting || !canPlay} className="yum-cta mt-6">
            {starting ? "…" : !canPlay ? "Déjà joué" : "Commencer 🧠"}
          </button>
          {!canPlay && !starting && <PlayLimitNotice />}
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <p className="text-white/90 text-sm font-semibold mb-3">Coups : {moves}</p>
          <div className="grid grid-cols-3 gap-3" data-testid="memory-grid">
            {cards.map((c, i) => {
              const show = c.matched || flipped.includes(i);
              return (
                <button
                  key={c.key}
                  data-testid={`memory-card-${i}`}
                  onClick={() => flip(i)}
                  className="w-20 h-24 sm:w-24 sm:h-28 rounded-2xl grid place-items-center text-4xl transition-transform active:scale-95"
                  style={show
                    ? { background: "#fff7ef", boxShadow: "0 6px 0 #a81a0c", opacity: c.matched ? 0.65 : 1 }
                    : { background: "linear-gradient(150deg,#ff5a45,#d8240f)", border: "3px solid #ffde59", boxShadow: "0 6px 0 rgba(0,0,0,.25)" }}
                >
                  {show ? c.emoji : <span className="font-display font-bold text-2xl text-sun">?</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <ClaimFlow result={result} onClose={reset} />
    </GameShell>
  );
}
